/**
 * The three prices the TP/SL modal shows: the mark price it has always used for
 * stats and validation, plus the index and last-trade prices the new "Trigger
 * by" selector puts side by side so the user can see the divergence before
 * choosing a source.
 *
 * All three are read from feeds that are already open on the trading page, and
 * are subscribed ONLY while the modal is on screen — a portfolio-page open of
 * this modal would otherwise pay for a week of trade history just to render one
 * number.
 */
import { useMemo } from "react";
import {
  GetAccount200ResponsePositionsInner,
  InstrumentTypeResponse,
} from "../../codegen-api";
import { AssetResponse } from "../../utils/asset";
import { rawToDecimal } from "../../utils/rawConversion";
import useIndexWSS from "../../hooks/wss/useIndexWSS";
import useMarkPriceWSS from "../../hooks/wss/useMarkPriceWSS";
import useTradesWSS from "../../hooks/wss/useTradesWSS";
import { useMarketDecimals } from "../../hooks/useMarketDecimals";
import { firstKnownPrice } from "../../utils/markGuards";

/**
 * The trades feed mixes already-display-scaled REST values with raw on-chain
 * 18-decimal integers. Same heuristic the orderbook's mid row uses.
 */
const looksRawInteger = (value?: string | number): boolean => {
  if (value === undefined || value === null) return false;
  const text = String(value);
  if (text.includes(".")) return false;
  return /^\d{10,}$/.test(text);
};

interface UseTPSLPricesParams {
  position?: GetAccount200ResponsePositionsInner;
  /** Only subscribe while the modal is actually visible. */
  active?: boolean;
}

export interface TPSLPrices {
  /** Used for the stats row and the existing trigger-vs-mark validation. */
  markPrice?: string | number;
  /** The oracle / index price — what `priceSource: "oracle"` watches. */
  indexPrice?: number;
  /** The market's last traded price — what `priceSource: "last"` watches. */
  lastPrice?: number;
}

export function useTPSLPrices({
  position,
  active = true,
}: UseTPSLPricesParams): TPSLPrices {
  const instrumentName = position?.instrument_name;

  const uniqueTickers = useMemo(
    () =>
      position && active
        ? {
            [position.instrument_name]: {
              asset: position.asset,
              derivative: position.option
                ? InstrumentTypeResponse.Option
                : InstrumentTypeResponse.Perpetual,
            },
          }
        : {},
    [position, active]
  );

  const { instrumentMark } = useMarkPriceWSS(uniqueTickers);
  // Live mark, else the position's; undefined when neither is known (null /
  // "0"), so the stats show "-" and the trigger-vs-mark check is skipped.
  const markPrice = firstKnownPrice(
    instrumentName ? instrumentMark[instrumentName]?.mark_price : undefined,
    position?.mark_price
  );

  const { index } = useIndexWSS(
    active ? (position?.asset as AssetResponse | undefined) : undefined
  );

  const { data: trades } = useTradesWSS(
    active ? instrumentName : undefined,
    undefined,
    true
  );

  const decimals = useMarketDecimals(instrumentName);

  const lastPrice = useMemo(() => {
    const raw = trades?.[0]?.price;
    if (!raw) return undefined;
    if (!looksRawInteger(raw)) {
      const parsed = Number(raw);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
    }
    // No decimals, no last price. This fed TP/SL trigger defaults and their
    // validation, so a guessed scale did not just mis-display a number — it
    // pre-filled an order with it.
    if (!decimals) return undefined;
    const parsed = Number(rawToDecimal(raw, decimals.quoteDecimals));
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
  }, [trades, decimals]);

  const indexPrice = useMemo(() => {
    const parsed = Number(index?.price);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
  }, [index?.price]);

  return { markPrice, indexPrice, lastPrice };
}
